/**
 * API Client pour la gestion des mentions légales
 */
import { safeFetch, safeJsonParse, getAuthHeaders } from './api';

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:3001/api/v1";


/**
 * Interface représentant une mention légale
 */
export interface LegalMention {
  id: string;
  tenantId: string;
  companyId?: string;
  code: string;
  name: string;
  content: string;
  category?: string;
  isActive: boolean;
  isDefault: boolean;
  createdAt: Date | string;
  updatedAt: Date | string;
  createdBy?: string;
  updatedBy?: string;
  // Relations optionnelles
  Company?: {
    id: string;
    legalName: string;
  };
}

/**
 * Interface pour les templates de mentions légales pré-définis
 */
export interface LegalMentionTemplate {
  code: string;
  name: string;
  category: string;
  content: string;
  description: string;
}

/**
 * Paramètres de filtre pour la liste des mentions légales
 */
export interface LegalMentionFilters {
  isActive?: boolean;
  category?: string;
  companyId?: string;
}

/**
 * Données pour créer une mention légale
 */
export interface CreateLegalMentionData {
  companyId?: string;
  code: string;
  name: string;
  content: string;
  category?: string;
  isActive?: boolean;
  isDefault?: boolean;
}

/**
 * Données pour mettre à jour une mention légale
 */
export interface UpdateLegalMentionData {
  name?: string;
  content?: string;
  category?: string;
  isActive?: boolean;
  isDefault?: boolean;
}

/**
 * Réponse de l'API pour une liste de mentions légales
 */
export interface LegalMentionsResponse {
  success: boolean;
  data: LegalMention[];
  count: number;
}

/**
 * Réponse de l'API pour une seule mention légale
 */
export interface LegalMentionResponse {
  success: boolean;
  data: LegalMention;
}

/**
 * Réponse de l'API pour les templates
 */
export interface LegalMentionTemplatesResponse {
  success: boolean;
  data: LegalMentionTemplate[];
  count: number;
}

/**
 * API Legal Mentions
 */
export const legalMentionsAPI = {
  /**
   * Récupérer toutes les mentions légales avec filtres optionnels
   */
  async getAll(filters?: LegalMentionFilters): Promise<LegalMentionsResponse> {
    const queryParams = new URLSearchParams();

    if (filters?.isActive !== undefined) {
      queryParams.append('isActive', filters.isActive.toString());
    }
    if (filters?.category) {
      queryParams.append('category', filters.category);
    }
    if (filters?.companyId) {
      queryParams.append('companyId', filters.companyId);
    }

    const url = `${API_URL}/legal-mentions${
      queryParams.toString() ? `?${queryParams.toString()}` : ""
    }`;

    const response = await safeFetch(url, {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(`Erreur lors de la récupération des mentions légales : ${response.statusText}`);
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse;
  },

  /**
   * Récupérer une mention légale par ID
   */
  async getById(legalMentionId: string): Promise<LegalMention> {
    const response = await safeFetch(`${API_URL}/legal-mentions/${legalMentionId}`, {
      method: "GET",
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(`Erreur lors de la récupération de la mention légale : ${response.statusText}`);
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.data;
  },

  /**
   * Créer une nouvelle mention légale
   */
  async create(data: CreateLegalMentionData): Promise<LegalMention> {
    const response = await safeFetch(`${API_URL}/legal-mentions`, {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await safeJsonParse(response).catch(() => ({ message: response.statusText }));
      throw new Error(error.message || `Erreur lors de la création de la mention légale`);
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.data;
  },

  /**
   * Mettre à jour une mention légale
   */
  async update(legalMentionId: string, data: UpdateLegalMentionData): Promise<LegalMention> {
    const response = await safeFetch(`${API_URL}/legal-mentions/${legalMentionId}`, {
      method: "PUT",
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await safeJsonParse(response).catch(() => ({ message: response.statusText }));
      throw new Error(error.message || `Erreur lors de la mise à jour de la mention légale`);
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.data;
  },

  /**
   * Supprimer une mention légale
   */
  async delete(legalMentionId: string): Promise<{ success: boolean; message: string }> {
    const response = await safeFetch(`${API_URL}/legal-mentions/${legalMentionId}`, {
      method: "DELETE",
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      const error = await safeJsonParse(response).catch(() => ({ message: response.statusText }));
      throw new Error(error.message || `Erreur lors de la suppression de la mention légale`);
    }

    return safeJsonParse(response);
  },

  /**
   * Définir une mention comme mention par défaut pour une company
   */
  async setAsDefault(legalMentionId: string, companyId: string): Promise<{ success: boolean; message: string }> {
    const response = await safeFetch(`${API_URL}/legal-mentions/${legalMentionId}/set-default`, {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify({ companyId }),
    });

    if (!response.ok) {
      const error = await safeJsonParse(response).catch(() => ({ message: response.statusText }));
      throw new Error(error.message || `Erreur lors de la définition de la mention par défaut`);
    }

    return safeJsonParse(response);
  },

  /**
   * Récupérer la mention légale par défaut d'une company
   */
  async getDefault(companyId: string): Promise<LegalMention | null> {
    const response = await safeFetch(`${API_URL}/legal-mentions/default?companyId=${companyId}`, {
      method: "GET",
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      if (response.status === 404) {
        return null;
      }
      throw new Error(`Erreur lors de la récupération de la mention par défaut : ${response.statusText}`);
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.data;
  },

  /**
   * Récupérer les templates de mentions légales pré-définis
   */
  async getTemplates(): Promise<LegalMentionTemplatesResponse> {
    const response = await safeFetch(`${API_URL}/legal-mentions/templates`, {
      method: "GET",
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(`Erreur lors de la récupération des templates : ${response.statusText}`);
    }

    return safeJsonParse(response);
  },

  /**
   * Créer une mention à partir d'un template pré-défini
   */
  async createFromTemplate(templateCode: string, companyId: string): Promise<LegalMention> {
    const response = await safeFetch(`${API_URL}/legal-mentions/from-template`, {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify({ templateCode, companyId }),
    });

    if (!response.ok) {
      const error = await safeJsonParse(response).catch(() => ({ message: response.statusText }));
      throw new Error(error.message || `Erreur lors de la création depuis le template`);
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.data;
  },
};
